"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CalendarCheck, Check, Plug, Repeat2, Sparkles } from "lucide-react";
import Link from "next/link";
import { LogoMark } from "@/components/logo";
import { Button } from "@/components/ui/button";

const EASE = [0.16, 1, 0.3, 1] as const;

// The hero leads with the clearest wedge over a horizontal, agency-built tool:
// Helios is the same caliber of platform, but built for fitness and set up for
// you. GoHighLevel has voice/SMS/email too, so voice is NOT the wedge here. We
// show only the positive (what fitness-native + done-for-you looks like in
// Helios); the honest head-to-head lives in the comparison section.
const SIGNALS = [
  { icon: CalendarCheck, label: "Bookings & no-shows", sub: "Native fitness data, not middleware" },
  { icon: Plug, label: "Booking system connected", sub: "No Zapier or webhooks to babysit" },
  { icon: Sparkles, label: "Set up for you", sub: "Playbooks built, not a blank canvas" },
] as const;

function HeroCopy() {
  const reduceMotion = useReducedMotion();
  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  };
  const containerProps = reduceMotion
    ? {}
    : { initial: "hidden" as const, animate: "visible" as const, variants: container };
  const iv = reduceMotion ? undefined : item;

  return (
    <motion.div
      className="flex max-w-xl flex-col items-center text-center xl:items-start xl:text-left"
      {...containerProps}
    >
      <motion.span
        variants={iv}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
      >
        <Repeat2 className="size-4" />
        GoHighLevel alternative
      </motion.span>

      <motion.h1
        variants={iv}
        className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-[2.75rem] lg:text-5xl"
      >
        Built for fitness, not agencies
      </motion.h1>

      <motion.p
        variants={iv}
        className="mt-6 max-w-lg text-balance leading-relaxed text-muted-foreground sm:text-lg"
      >
        GoHighLevel is a powerful all-in-one, built for agencies to assemble and resell. Helios is
        that caliber of platform built for fitness, and set up for you: your booking and membership
        data native, your lifecycle playbooks ready on day one.
      </motion.p>

      <motion.div
        variants={iv}
        className="mt-8 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row xl:items-start"
      >
        <Button asChild size="xl" className="group w-full sm:w-auto">
          <Link href="/book-a-demo">
            Book a demo
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Button>
        <Button asChild size="xl" variant="outline" className="w-full sm:w-auto">
          <Link href="#comparison">See the comparison</Link>
        </Button>
      </motion.div>

      <motion.p variants={iv} className="mt-5 text-xs font-medium text-muted-foreground">
        Ask about our white-glove migration service.
      </motion.p>
    </motion.div>
  );
}

function ProfileCard() {
  const reduceMotion = useReducedMotion();
  const rowAnim = (i: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 10 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, ease: EASE, delay: 0.5 + i * 0.12 },
        };

  return (
    <div className="w-[300px] rounded-2xl border border-border bg-card p-5 shadow-xl sm:w-[332px]">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <span className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-primary dark:text-brand-accent">
          <LogoMark className="size-4" />
        </span>
        <div>
          <div className="text-sm font-bold leading-tight text-foreground">Helios</div>
          <div className="text-xs leading-tight text-muted-foreground">Built for fitness</div>
        </div>
      </div>

      <ul className="mt-4 space-y-3">
        {SIGNALS.map(({ icon: Icon, label, sub }, i) => (
          <motion.li key={label} className="flex items-center gap-3" {...rowAnim(i)}>
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground">
              <Icon className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground">{label}</div>
              <div className="truncate text-xs text-muted-foreground">{sub}</div>
            </div>
            <Check strokeWidth={3} className="size-4 shrink-0 text-green-600 dark:text-green-500" />
          </motion.li>
        ))}
      </ul>

      <div className="mt-4 rounded-lg border border-border bg-background px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">
        Built for fitness, set up for you
      </div>
    </div>
  );
}

function Stage() {
  const reduceMotion = useReducedMotion();
  const cardAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: 24, scale: 0.98 },
        animate: { opacity: 1, x: 0, scale: 1 },
        transition: { duration: 0.8, ease: EASE, delay: 0.3 },
      };

  return (
    <div className="relative flex w-full justify-center xl:justify-end">
      {/* Faded duplicate behind for depth — purely decorative. */}
      <div
        aria-hidden
        className="absolute left-1/2 top-6 hidden h-full w-[300px] -translate-x-1/2 rounded-2xl border border-border bg-muted/30 sm:w-[332px] xl:left-auto xl:right-6 xl:translate-x-0 xl:block"
      />
      <motion.div {...cardAnim} className="relative z-10">
        <ProfileCard />
      </motion.div>
    </div>
  );
}

export function GohighlevelHero() {
  return (
    <section className="relative overflow-x-clip border-b border-border bg-background pb-16 pt-28 sm:pb-20 lg:pt-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 lg:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)] xl:gap-10">
        <HeroCopy />
        <Stage />
      </div>
    </section>
  );
}
